import { Avatar, SvgIcon } from '@elementary/common-ui/components';
import { ReactComponent as UserIcon } from '@elementary/common-ui/assets/16x16/user.svg';
import { ReactComponent as SlackIcon } from '@elementary/common-ui/assets/16x16/slack.svg';
import { ReactComponent as TeamsIcon } from '@elementary/common-ui/assets/16x16/teams.svg';
import { ReactComponent as PagerDutyIcon } from '@elementary/common-ui/assets/16x16/pager.svg';
import { ReactComponent as OpsgenieIcon } from '@elementary/common-ui/assets/16x16/opsgenie.svg';
import { ReactComponent as EmailIcon } from '@elementary/common-ui/assets/16x16/email.svg';
import { ReactComponent as EditIcon } from '@elementary/common-ui/assets/16x16/edit-2.svg';
import { ReactComponent as EventIcon } from '@elementary/common-ui/assets/16x16/severity.svg';
import { ReactComponent as PlusIcon } from '@elementary/common-ui/assets/16x16/plus.svg';
import { ReactComponent as DeleteIcon } from '@elementary/common-ui/assets/16x16/delete-trash.svg';
import { ReactComponent as SyncSuccessIcon } from '@elementary/common-ui/assets/16x16/sync-success.svg';
import { localizeDate } from '@elementary/common-ui/utils/datetime';
import {
  extractTicketIdFromUrl,
  ticketingIconMap,
} from 'shared/components/ticketing/utils';
import {
  formatAlertDestinationChannel,
  getAlertDestinationLabel,
  severityIconMap,
  statusIconMap,
  toSafeSlackUrl,
} from 'incidents/utils';
import type {
  EventLabelResult,
  EventLink,
} from 'incidents/components/TimelineEventLabel';
import type { Severity, Status } from 'incidents/types';
import type { FetchTeamUsersApi } from 'shared/types';
import type { AlertIntegrationType } from '../../types';
import { STATUS_DISPLAY, SEVERITY_DISPLAY } from '../../constants';
import type {
  EventGroup,
  IncidentDetailEvent,
  IncidentEventType,
} from '../../types';

const ICON_SIZE = 16;

export const formatDate = (date?: string) =>
  localizeDate(date).format('MMM D, h:mm A');

const getAlertIcon = (integrationType?: AlertIntegrationType) => {
  switch (integrationType) {
    case 'SLACK':
      return <SlackIcon />;
    case 'MICROSOFT_TEAMS':
      return <TeamsIcon />;
    case 'PAGER_DUTY':
      return <PagerDutyIcon />;
    case 'OPSGENIE':
      return <OpsgenieIcon />;
    case 'EMAIL':
      return <EmailIcon />;
    default:
      return <EmailIcon />;
  }
};

const getStatusIcon = (status?: string) => {
  const key = status?.toLowerCase() ?? '';
  const display = (STATUS_DISPLAY[key] || 'Open') as Status;
  return statusIconMap[display] ?? null;
};

const getSeverityIcon = (severity?: string) => {
  const key = severity?.toLowerCase() ?? '';
  const display = (SEVERITY_DISPLAY[key] || 'Normal') as Severity;
  return severityIconMap[display] ?? null;
};

const getAssigneeIcon = (
  assigneeEmail?: string | null,
  usersData?: FetchTeamUsersApi,
) => {
  if (!assigneeEmail) {
    return (
      <SvgIcon width={ICON_SIZE}>
        <UserIcon />
      </SvgIcon>
    );
  }
  const user = usersData?.find((u) => u.email === assigneeEmail);
  return (
    <Avatar
      value={{
        id: user?.id || assigneeEmail,
        name: user?.name || assigneeEmail,
      }}
      size="extraSmall"
    />
  );
};

const getStatusFromEventData = (
  data: Record<string, unknown>,
): string | undefined => {
  const raw = data.new_status;
  return typeof raw === 'string' ? raw : undefined;
};

const getTicketServiceKey = (data: Record<string, unknown>): string => {
  const raw = data.service;
  return typeof raw === 'string' ? raw.toLowerCase().replace('_', '') : '';
};

const formatTicketingServiceLabel = (serviceKey: string): string => {
  switch (serviceKey) {
    case 'jira':
      return 'Jira';
    case 'linear':
      return 'Linear';
    case 'servicenow':
      return 'ServiceNow';
    default:
      return serviceKey || 'ticket';
  }
};

const getTicketEventLabelParts = (data: Record<string, unknown>) => {
  const serviceKey = getTicketServiceKey(data);
  const ticketId =
    typeof data.service_ticket_id === 'string' ? data.service_ticket_id : '';
  const ticketUrl =
    typeof data.service_ticket_url === 'string' ? data.service_ticket_url : '';
  const id = ticketUrl
    ? extractTicketIdFromUrl(ticketUrl, serviceKey, ticketId) || ticketId
    : ticketId;
  return {
    serviceKey,
    serviceLabel: formatTicketingServiceLabel(serviceKey),
    id,
  };
};

export const getEventIcon = (
  event: IncidentDetailEvent,
  usersData?: FetchTeamUsersApi,
) => {
  const data = event.data ?? {};

  switch (event.type) {
    case 'ASSIGNEE_CHANGE':
      return getAssigneeIcon(data.assignee as string | undefined, usersData);
    case 'STATUS_CHANGE':
      return (
        <SvgIcon width={ICON_SIZE}>
          {getStatusIcon(getStatusFromEventData(data)) ?? <EventIcon />}
        </SvgIcon>
      );
    case 'SEVERITY_CHANGE':
      return (
        <SvgIcon width={ICON_SIZE}>
          {getSeverityIcon(data.severity as string | undefined) ?? (
            <EventIcon />
          )}
        </SvgIcon>
      );
    case 'ALERT_SENT':
      return (
        <SvgIcon width={ICON_SIZE}>
          {getAlertIcon(
            (data.destination as { type?: AlertIntegrationType } | undefined)
              ?.type,
          )}
        </SvgIcon>
      );
    case 'SIGNAL_ADDED':
      return (
        <SvgIcon width={ICON_SIZE}>
          <PlusIcon />
        </SvgIcon>
      );
    case 'SIGNAL_REMOVED':
      return (
        <SvgIcon width={ICON_SIZE}>
          <DeleteIcon />
        </SvgIcon>
      );
    case 'SIGNAL_RESOLVED':
      return (
        <SvgIcon width={ICON_SIZE}>
          <SyncSuccessIcon />
        </SvgIcon>
      );
    case 'TITLE_CHANGE':
    case 'SUMMARY_GENERATED':
    case 'SUMMARY_EDITED':
    case 'SUMMARY_DELETED':
    case 'SUMMARY_SENT':
      return (
        <SvgIcon width={ICON_SIZE}>
          <EditIcon />
        </SvgIcon>
      );
    case 'TICKET_LINKED':
    case 'TICKET_UNLINKED':
    case 'TICKET_CLOSED':
    case 'TICKET_REOPENED': {
      const sk = getTicketServiceKey(data);
      const icon = ticketingIconMap[sk];
      return <SvgIcon width={ICON_SIZE}>{icon ?? <EventIcon />}</SvgIcon>;
    }
    case 'RESOLVED_BY_TICKETS':
      return (
        <SvgIcon width={ICON_SIZE}>
          <EventIcon />
        </SvgIcon>
      );
    default:
      return (
        <SvgIcon width={ICON_SIZE}>
          <EventIcon />
        </SvgIcon>
      );
  }
};

const formatChangedBy = (event: IncidentDetailEvent): string => {
  const name = event.actor?.name || event.actor?.email;
  return name ? ` by ${name}` : '';
};

const formatValue = (value?: string | null): string =>
  value ? ` to '${value}'` : '';

const getUserNameByEmail = (
  email: string | null | undefined,
  usersData?: FetchTeamUsersApi,
): string | undefined => {
  if (!email || !usersData) return undefined;
  return usersData.find((u) => u.email === email)?.name;
};

type EventLabelHelpers = {
  onOpenIncident?: (incidentId: string) => void;
};

const getRelatedIncidentLink = (
  incidentId: string | undefined,
  linkedText: string,
  helpers?: EventLabelHelpers,
): EventLink | undefined => {
  if (!incidentId || !helpers?.onOpenIncident) {
    return undefined;
  }
  return {
    linkedText,
    onClick: () => helpers.onOpenIncident?.(incidentId),
  };
};

export const getEventLabel = (
  event: IncidentDetailEvent,
  usersData?: FetchTeamUsersApi,
  helpers?: EventLabelHelpers,
): EventLabelResult => {
  const data = event.data ?? {};

  const labelByType: Record<IncidentEventType, () => EventLabelResult> = {
    STATUS_CHANGE: () => {
      const statusRaw = getStatusFromEventData(data);
      const display =
        statusRaw !== undefined
          ? STATUS_DISPLAY[statusRaw.toLowerCase()] ?? statusRaw
          : '';
      const viaJira =
        (data.source as string | undefined) === 'jira_webhook'
          ? ' via Jira'
          : '';
      return {
        label:
          'Status changed' +
          formatValue(display) +
          viaJira +
          formatChangedBy(event),
      };
    },
    ASSIGNEE_CHANGE: () => {
      const assignee = data.assignee as string | null | undefined;
      const displayName = getUserNameByEmail(assignee, usersData) ?? assignee;
      const value = formatValue(displayName);
      if (value) {
        return { label: 'Assigned' + value + formatChangedBy(event) };
      }
      return { label: 'Assignee removed' + formatChangedBy(event) };
    },
    SEVERITY_CHANGE: () => {
      const severity = data.severity as string | undefined;
      const display =
        severity !== undefined
          ? SEVERITY_DISPLAY[severity.toLowerCase()] ?? severity
          : '';
      return {
        label:
          'Severity changed' + formatValue(display) + formatChangedBy(event),
      };
    },
    SIGNAL_ADDED: () => {
      const relatedIncidentId = data.related_incident_id as string | undefined;
      const relatedTitle = data.related_incident_title as string | undefined;
      const linkedText = relatedTitle
        ? `"${relatedTitle}"`
        : 'another incident';
      const fromPart = relatedIncidentId ? ` from ${linkedText}` : '';
      return {
        label: `Monitor added${fromPart}${formatChangedBy(event)}`,
        link: getRelatedIncidentLink(relatedIncidentId, linkedText, helpers),
      };
    },
    SIGNAL_REMOVED: () => {
      const relatedIncidentId = data.related_incident_id as string | undefined;
      const relatedTitle = data.related_incident_title as string | undefined;
      const linkedText = relatedTitle
        ? `"${relatedTitle}"`
        : 'another incident';
      const toPart = relatedIncidentId ? ` to ${linkedText}` : '';
      return {
        label: `Monitor removed${toPart}${formatChangedBy(event)}`,
        link: getRelatedIncidentLink(relatedIncidentId, linkedText, helpers),
      };
    },
    SIGNAL_RESOLVED: () => ({
      label: `Monitor resolved${formatChangedBy(event)}`,
    }),
    ALERT_SENT: () => {
      const destination = data.destination as
        | { type?: AlertIntegrationType; channel?: string }
        | undefined;
      const destinationLabel = getAlertDestinationLabel(destination?.type);
      const channelInfo = formatAlertDestinationChannel(destination);
      const safeSlackUrl = toSafeSlackUrl(
        data.slack_permalink as string | undefined,
      );
      const link: EventLink | undefined =
        destination?.type === 'SLACK' && safeSlackUrl && destination.channel
          ? {
              url: safeSlackUrl,
              tooltip: 'Open in Slack',
              linkedText: `#${destination.channel}`,
            }
          : undefined;
      return {
        label: 'Alert sent' + destinationLabel + channelInfo,
        link,
      };
    },
    TITLE_CHANGE: () => {
      const newTitle = data.new_title as string | undefined;
      return {
        label: newTitle ? `Title changed to "${newTitle}"` : 'Title changed',
      };
    },
    SUMMARY_GENERATED: () => ({ label: 'Summary generated' }),
    SUMMARY_EDITED: () => ({
      label: 'Summary edited' + formatChangedBy(event),
    }),
    SUMMARY_DELETED: () => ({
      label: 'Summary deleted' + formatChangedBy(event),
    }),
    SUMMARY_SENT: () => ({ label: 'Summary sent' }),
    TICKET_LINKED: () => {
      const { serviceLabel, id } = getTicketEventLabelParts(data);
      const idPart = id ? ` ${id}` : '';
      return {
        label: `Linked ${serviceLabel} ticket${idPart}${formatChangedBy(
          event,
        )}`,
      };
    },
    TICKET_UNLINKED: () => {
      const { serviceLabel, id } = getTicketEventLabelParts(data);
      const idPart = id ? ` ${id}` : '';
      return {
        label: `Unlinked ${serviceLabel} ticket${idPart}${formatChangedBy(
          event,
        )}`,
      };
    },
    TICKET_CLOSED: () => {
      const { serviceLabel, id } = getTicketEventLabelParts(data);
      const idPart = id ? ` ${id}` : '';
      return {
        label: `Closed ${serviceLabel} ticket${idPart}${formatChangedBy(
          event,
        )}`,
      };
    },
    TICKET_REOPENED: () => {
      const { serviceLabel, id } = getTicketEventLabelParts(data);
      const idPart = id ? ` ${id}` : '';
      return {
        label: `Reopened ${serviceLabel} ticket${idPart}${formatChangedBy(
          event,
        )}`,
      };
    },
    RESOLVED_BY_TICKETS: () => ({
      label: `Resolved by tickets${formatChangedBy(event)}`,
    }),
  };

  return (labelByType[event.type] ?? (() => ({ label: event.type })))();
};

export const getEventLink = (
  event: IncidentDetailEvent,
  usersData?: FetchTeamUsersApi,
  helpers?: EventLabelHelpers,
): EventLink | undefined => getEventLabel(event, usersData, helpers).link;

export const groupConsecutiveEvents = (
  sortedEvents: IncidentDetailEvent[],
  usersData?: FetchTeamUsersApi,
  helpers?: EventLabelHelpers,
): EventGroup[] => {
  if (sortedEvents.length === 0) return [];

  const groups: EventGroup[] = [];
  let currentGroup: IncidentDetailEvent[] = [sortedEvents[0]];
  let current = getEventLabel(sortedEvents[0], usersData, helpers);

  for (let i = 1; i < sortedEvents.length; i++) {
    const event = sortedEvents[i];
    const result = getEventLabel(event, usersData, helpers);

    if (result.label === current.label) {
      currentGroup.push(event);
    } else {
      groups.push({
        events: currentGroup,
        label: current.label,
        link: current.link,
        firstEvent: currentGroup[0],
        lastEvent: currentGroup[currentGroup.length - 1],
      });
      currentGroup = [event];
      current = result;
    }
  }

  groups.push({
    events: currentGroup,
    label: current.label,
    link: current.link,
    firstEvent: currentGroup[0],
    lastEvent: currentGroup[currentGroup.length - 1],
  });

  return groups;
};
